/**
 * Page-local URL param for WorkloadPage's one list-affecting piece of state that was NOT surviving
 * back-navigation or a refresh: the tax-year selector (drives the `taxReturns` query key). Fifth
 * adoption of the pattern #78 established (after #97, #98, #99).
 *
 * The reassign-dialog state (`reassignDialogOpen`, `selectedPreparer`, `targetPreparer`,
 * `statusToReassign`) is deliberately OUT of scope: it's reset together `onSuccess` of the
 * reassign mutation, tied to one bulk action, not view configuration -- same call made for
 * row-selection state on other pages in this sweep.
 *
 * Conventions (mirroring the other hooks in this sweep):
 * - `year=<value>` is present only when it differs from the page's own default
 *   (currentYear - 1); an unparseable value falls back to that default, never NaN.
 * - Mutations replace history (never push).
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const currentYear = new Date().getFullYear();
const defaultYear = currentYear - 1;

export interface WorkloadScopeParams {
  year: number;
  setYear: (year: number) => void;
}

export function useWorkloadScopeParams(): WorkloadScopeParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const year = useMemo(() => {
    const raw = searchParams.get('year');
    if (!raw) return defaultYear;
    const parsed = Number(raw);
    return Number.isInteger(parsed) ? parsed : defaultYear;
  }, [searchParams]);

  const setYear = useCallback(
    (next: number) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === defaultYear) params.delete('year');
          else params.set('year', String(next));
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return { year, setYear };
}
